import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useMostPopulatedSearchExperienceCategories from 'components/Experiences/hooks/useMostPopulatedSearchExperienceCategories'
import OfferCarouselHeader from '../common/OfferCarouselHeader'
import OfferCarouselFilters from '../common/OfferCarouselFilters'
import LayoutContainer from 'components/Common/LayoutContainer'
import { rem } from 'polished'
import styled from 'styled-components'
import { fillArray, take } from 'lib/array/arrayUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FullWidthCarouselWithAnalytics from 'components/WithAnalytics/Carousel/FullWidthCarouselWithAnalytics'
import OfferCarouselLookingForSomethingElse from 'home/components/LookingForSomethingElse/OfferCarouselLookingForSomethingElse'
import { LFSEOfferType } from 'home/components/LookingForSomethingElse/constants'
import ExperienceOfferCard from '../common/OfferCards/ExperienceOfferCard'
import { CarouselCardLoadingSkeleton } from 'home/components/CarouselCard/CarouselCardLoadingSkeleton'
import useExperienceSearchList from 'hooks/Experiences/useExperienceSearchList'
import { useInView } from 'react-intersection-observer'
import { CAROUSEL_IN_VIEW_PARAMS } from 'home/constants'
import FullWidthCarousel from 'components/Luxkit/Carousel/FullWidthCarousel'

const ExperienceOfferCarouselContainer = styled.div`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

interface Props {
  place: App.Place | undefined;
  ExperienceHeading: JSX.Element;
  addImpression?: boolean;
  campaigns?: Array<string>;
  showFilters: boolean;
}

function ExperienceOfferCarousel({ ExperienceHeading, place, addImpression, campaigns, showFilters }: Props) {
  const [selectedCategory, setSelectedCategory] = useState<App.ExperienceItemCategory>()
  const [categories] = useMostPopulatedSearchExperienceCategories(place)
  const listLimit = 8

  const { ref: inViewRef, inView } = useInView(CAROUSEL_IN_VIEW_PARAMS)

  const list = useExperienceSearchList({
    placeId: place?.id,
    enabled: !!selectedCategory,
    campaigns,
    categoryCodes: selectedCategory ? [selectedCategory.id] : undefined,
  })

  const toggleFilter = useCallback((name: string) => {
    const newCategory = (categories.find(category => category.parent.name === name) || categories[0]).parent
    setSelectedCategory(newCategory)
  }, [categories])

  useEffect(() => {
    if (categories?.length > 0 && showFilters) {
      setSelectedCategory(categories[0].parent)
    }
  }, [categories, showFilters])

  const categoryNames = useMemo(() => categories.map(category => category.parent.name), [categories])

  return (
    <ExperienceOfferCarouselContainer>
      <VerticalSpacer gap={20}>
        <LayoutContainer>
          <VerticalSpacer gap={20}>
            <OfferCarouselHeader allPath={place ? `/search/experiences?destinationId=${place.id}&destinationName=${place.name}` : '/experiences'}>
              {ExperienceHeading}
            </OfferCarouselHeader>
            {showFilters && <OfferCarouselFilters
              options={categoryNames}
              active={selectedCategory?.name}
              onClick={toggleFilter}
            />}
          </VerticalSpacer>
        </LayoutContainer>
        {(list.fetching || !inView) &&
          <FullWidthCarousel snap="start" ref={inViewRef}>
            {fillArray(4).map((_, index) => <CarouselCardLoadingSkeleton key={index} />)}
          </FullWidthCarousel>}
        {!list.fetching &&
          <FullWidthCarouselWithAnalytics snap="start" itemsPerArrow={3}>
            {take(list.ids, listLimit).map((id, index) =>
              <ExperienceOfferCard
              key={id}
              id={id}
              index={index}
              loadIfInView
              analytics={addImpression}
              inViewThreshold={CAROUSEL_IN_VIEW_PARAMS.threshold}
              inViewVerticalRootMargin={CAROUSEL_IN_VIEW_PARAMS.rootMargin}
            />,
            )}
            <OfferCarouselLookingForSomethingElse offerType={LFSEOfferType.Experiences} />
          </FullWidthCarouselWithAnalytics>
          }
      </VerticalSpacer>
    </ExperienceOfferCarouselContainer>
  )
}

ExperienceOfferCarousel.defaultProps = {
  showFilters: true,
}

export default ExperienceOfferCarousel
